/**
 * D2 -- a repaired connector, and nobody to escalate to.
 *
 * Two fourth-round complaints on the same panel.
 *
 * A connector that had been fixed showed the new configuration beside
 * the old error and offered nothing: the operator had done the work and
 * had no way to tell whether it had taken, short of guessing that "Run
 * now" was the next move. The product knows -- it holds when the
 * settings last changed and when the last run was.
 *
 * And the escalation line said "your platform administrator" TO the
 * platform administrator. Telling somebody to escalate to themselves is
 * worse than saying nothing: it reads as a dead end where the real
 * answer is that no owner is recorded for this feed, which is a gap he
 * can close in Bank Setup.
 */

export interface NudgeInput {
  /** When the connector's settings were last changed. */
  settingsChangedAt: string | null | undefined;
  /** When it last RAN, successful or not. */
  lastRunAt: string | null | undefined;
  lastRunStatus: string | null | undefined;
}

/**
 * True when the settings have moved since a run that did not succeed --
 * the "you fixed it, try again" case, and only that case. A connector
 * whose last run SUCCEEDED does not need a nudge, and one that has
 * never run is a different sentence (it has never been tried at all).
 */
export function needsRerun({
  settingsChangedAt,
  lastRunAt,
  lastRunStatus,
}: NudgeInput): boolean {
  if (!settingsChangedAt || !lastRunAt) return false;
  if (lastRunStatus === "succeeded") return false;
  const changed = new Date(settingsChangedAt).getTime();
  const ran = new Date(lastRunAt).getTime();
  if (Number.isNaN(changed) || Number.isNaN(ran)) return false;
  return changed > ran;
}

export interface EscalationTarget {
  /** A named person, when the register records one. */
  who: string | null;
  /** True when the viewer is the person they would be told to tell. */
  isSelf: boolean;
}

/**
 * Who to escalate to, honestly.
 *
 * A recorded owner is always the answer. Without one, an administrator
 * is being pointed at himself, which is a gap in the data-source
 * register rather than an escalation path.
 */
export function escalationTarget(
  owner: string | null | undefined,
  viewerIsAdmin: boolean,
): EscalationTarget {
  const named = (owner ?? "").trim();
  if (named) return { who: named, isSelf: false };
  return { who: null, isSelf: viewerIsAdmin };
}
